import React from 'react';
import { Dialog, DialogContent, IconButton, Box, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RootState, useAppDispatch, useAppSelector } from '../../../state/state';
import { closeModal } from '../../../state/ModalSlice/ModalSlice';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { toast } from 'react-toastify';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

const DeleteCustomerKYCFieldModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state: RootState) => state.ModalSlice.DeleteCustomerKYCFieldModalOpen);
  const serviceProviderId = useAppSelector(selectEffectiveUID); 
  const { questionId } = useAppSelector((state: RootState) => state.ArbitrarySlice.ForDeleteCustomerKYCFieldModal);

  const handleClose = () => {
    dispatch(closeModal("DeleteCustomerKYCFieldModalOpen"));
  };

  const handleDelete = async () => {
    try {
      const serviceProviderRef = doc(db, 'ServiceProviders', serviceProviderId);
      
      // Fetch current document
      const serviceProviderDoc = await getDoc(serviceProviderRef);
      if (!serviceProviderDoc.exists()) {
        throw new Error('Service provider not found');
      }

      const currentFields = serviceProviderDoc.data().customerKYCFields || [];
      const updatedFields = currentFields.filter((f: any) => f.id !== questionId);

      // Update with filtered array
      await updateDoc(serviceProviderRef, {
        customerKYCFields: updatedFields
      });

      toast.success('KYC field deleted successfully');
      handleClose();
    } catch (error) {
      console.error('Error deleting custom field:', error);
      toast.error('Error deleting field');
    }
  };

  return (
    <Dialog
      sx={{
        padding: 0,
        '& .MuiPaper-root': {
          borderRadius: '10px',
        },
      }}
      maxWidth="sm"
      open={isOpen}
      PaperProps={{
        component: 'div',
        className: 'relative',
      }}
    >
      <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className="bg-white">
        <div className="w-[400px]">
          <div className="header pb-[15px] space-y-2">
            <h2 className="text-2xl font-semibold text-gray-800 transition-all duration-300">
              Delete KYC Field
            </h2>
            <p className="text-gray-500 font-medium">
              Are you sure you want to delete this custom field? This action cannot be undone.
            </p>
          </div>

          <div className="absolute right-4 top-4">
            <IconButton 
              onClick={handleClose}
              className="hover:rotate-90 transition-transform duration-300"
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                }
              }}
            >
              <CloseIcon className="text-gray-500 hover:text-gray-700" />
            </IconButton>
          </div>

          <Box className="flex gap-3 justify-end pt-6">
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                px: 3,
                py: 1,
                borderColor: 'grey.300',
                '&:hover': {
                  borderColor: 'grey.400'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                px: 3,
                py: 1,
                backgroundColor: '#dc2626',
                '&:hover': {
                  backgroundColor: '#b91c1c'
                }
              }}
            >
              Delete Field
            </Button>
          </Box>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCustomerKYCFieldModal;
